import type { ReactNode } from 'react';

import type { FormInstance } from '@arco-design/web-react';

import type { CapabilityBinding, DeployAccessPolicy } from '../../core/types';
import { buildCapabilityControl } from './buildCapabilityControl';
import { buildFormPaths } from './buildFormPaths';
import { getCapability } from './registry';

export interface RenderCapabilityModuleItem {
  key: string;
  capabilities: readonly CapabilityBinding[];
}

export interface RenderCapabilityModulesParams<TField extends string = string> {
  form: FormInstance;
  /** 已按 scene / modules filter 筛过的可见模块 */
  modules: readonly RenderCapabilityModuleItem[];
  accessPolicy: DeployAccessPolicy<TField>;
  blockMode?: 'edit' | 'preview';
  /** 已合并的 scene + props capabilityOptions（按 capability key） */
  capabilityOptions?: Partial<Record<string, Record<string, unknown>>>;
  /** 按 capability key 注入的 linkage props（各插件 LinkagePropsMap） */
  linkageProps?: Partial<Record<string, unknown>>;
  hideEmptyList?: boolean;
  /** 仅渲染指定 capability key */
  capabilityKeys?: string[];
  /** 按 binding 追加 options（如 CI 镜像选中回调） */
  resolveExtraOptions?: (bindingKey: string, moduleKey: string) => Record<string, unknown> | undefined;
  /**
   * 模块级插槽：在 registry 能力节点基础上插入自定义节点（如 MI/TC 网卡、runtime）。
   * 默认原样返回 capabilityNodes。
   */
  renderModuleExtras?: (moduleKey: string, capabilityNodes: ReactNode[]) => ReactNode[];
}

/**
 * ResourceEditor 公共循环：getCapability → paths → control → 渲染能力块。
 * 不引入 Editor 引擎；插件仍负责 scene / linkage / OptionMetaRootField。
 */
export function renderCapabilityModules<TField extends string>(params: RenderCapabilityModulesParams<TField>): ReactNode[] {
  const {
    form,
    modules,
    accessPolicy,
    blockMode = 'edit',
    capabilityOptions,
    linkageProps,
    hideEmptyList,
    capabilityKeys,
    resolveExtraOptions,
    renderModuleExtras,
  } = params;

  const capabilityFilter = capabilityKeys?.length ? new Set(capabilityKeys) : undefined;

  return modules.flatMap((mod): ReactNode[] => {
    const capabilityNodes = mod.capabilities
      .filter((binding) => !capabilityFilter || capabilityFilter.has(String(binding.key)))
      .map((binding) => {
        const bindingKey = String(binding.key);
        const def = getCapability(bindingKey);
        if (!def) return null;
        const Cap = def.component;
        const paths = buildFormPaths(binding, def);
        const control = buildCapabilityControl(binding, accessPolicy);
        const bindingOptions = ('options' in binding ? binding.options : undefined) as Record<string, unknown> | undefined;
        const mergedOptions = {
          ...bindingOptions,
          ...(capabilityOptions?.[bindingKey] ?? {}),
          ...(resolveExtraOptions?.(bindingKey, mod.key) ?? {}),
        };
        return (
          <Cap
            key={`${mod.key}-${bindingKey}`}
            form={form}
            paths={paths}
            options={mergedOptions}
            linkageProps={linkageProps?.[bindingKey] as Record<string, unknown> | undefined}
            blockMode={blockMode}
            hideEmptyList={hideEmptyList}
            {...control}
          />
        );
      });

    return renderModuleExtras ? renderModuleExtras(mod.key, capabilityNodes) : capabilityNodes;
  });
}
